/**
 * 🎯 useTemporaryAssignments Hook
 * 
 * Task #77: Data hook for temporary coverage management
 * 
 * The heartbeat of the coverage system - provides reactive access to:
 * - Active coverages with real-time status
 * - Coverages expiring soon (1-day, 3-day warnings)
 * - Reminder-based coverages needing attention
 * - History of ended/extended coverages
 * 
 * Actions:
 * - Create fixed-end or reminder-based coverage
 * - Extend coverage duration
 * - End coverage early
 * - Make coverage permanent
 * - Mark reminders as sent
 * 
 * UI Features:
 * - Progress bars (days elapsed / total)
 * - Days remaining calculations
 * - Quick action helpers
 * - Role-aware filtering
 * - Offline caching
 * 
 * @module useTemporaryAssignments
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getCompanyStorageKey } from '../utils/tenantContext';

// Service imports
import {
  // Coverage operations
  createTemporaryAssignment,
  createReminderBasedCoverage,
  endTemporaryAssignment,
  extendTemporaryAssignment,
  makeCoveragePermanent,
  // Queries
  getActiveCoverages,
  getCoverageById,
  markReminderSent,
  getActiveCoverageByPumper,
  getActiveCoverageForPumper,
  // Types
  type TemporaryCoverageResult,
  type FixedEndDateCoverageInput,
  type ReminderBasedCoverageInput,
} from '../utils/services/wellAssignmentService';

import type {
  TemporaryAssignmentDocument,
  TemporaryAssignmentStatus,
  CoverageReason,
  CoverageEndReason,
  CoverageEndAction,
} from '../types/wellAssignment';

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Coverage urgency levels for UI display
 */
export type CoverageUrgency = 
  | 'normal'           // More than 3 days remaining
  | 'ending_soon'      // 1-3 days remaining
  | 'ending_today'     // Less than 24 hours
  | 'overdue'          // Past end date but not ended
  | 'reminder_due'     // Reminder date passed
  | 'indefinite';      // No end date (reminder-based)

/**
 * Enhanced coverage info for UI consumption
 */
export interface CoverageInfo {
  // Core data
  id: string;
  coveringPumperId: string;
  coveringPumperName: string;
  originalPumperId: string;
  originalPumperName: string;
  wellIds: string[];
  wellNames: string[];
  wellCount: number;
  
  // Reason & context
  reason: CoverageReason;
  reasonNote?: string;
  
  // Dates
  startDate: Date;
  endDate: Date | null;
  reminderDate: Date | null;
  endedAt?: Date;
  
  // Status
  status: TemporaryAssignmentStatus;
  urgency: CoverageUrgency;
  isReminderBased: boolean;
  reminderSent: boolean;
  
  // End configuration
  endAction: CoverageEndAction;
  endReason?: CoverageEndReason;
  endedBy?: string;
  endedByName?: string;
  
  // Calculated UI values
  daysElapsed: number;
  daysRemaining: number | null;
  totalDays: number | null;
  progressPercent: number | null;
  daysUntilReminder: number | null;
  
  // Metadata
  createdBy: string;
  createdByName?: string;
  createdAt: Date;
}

/**
 * Summary stats for dashboard
 */
export interface CoverageSummary {
  totalActive: number;
  endingToday: number;
  endingSoon: number;      // 1-3 days
  remindersDue: number;
  reminderBased: number;   // Open-ended coverages
  extended: number;
  byReason: Record<CoverageReason, number>;
}

/**
 * Pumper with coverage info
 */
export interface PumperCoverageInfo {
  id: string;
  name: string;
  /** Coverage this pumper is providing */
  providingCoverage: CoverageInfo | null;
  /** Coverage being received by this pumper */
  receivingCoverage: CoverageInfo | null;
  /** Can this pumper provide coverage? (not already providing) */
  canProvideCoverage: boolean;
  /** Is this pumper being covered? */
  isBeingCovered: boolean;
}

/**
 * Hook options
 */
export interface UseTemporaryAssignmentsOptions {
  /** Current user for role/permission checks */
  user: {
    id: string;
    role: string;
    companyId?: string;
  } | null;
  /** Auto-refresh interval in ms (0 = disabled) */
  refreshInterval?: number;
  /** Filter to specific covering pumper */
  coveringPumperId?: string;
  /** Filter to specific original pumper */
  originalPumperId?: string;
  /** Include ended coverages in results */
  includeEnded?: boolean;
  /** Days threshold for "ending soon" (default: 3) */
  endingSoonThreshold?: number;
}

/**
 * Hook state
 */
export interface UseTemporaryAssignmentsState {
  // Data
  coverages: CoverageInfo[];
  endingSoon: CoverageInfo[];
  remindersDue: CoverageInfo[];
  history: CoverageInfo[];
  summary: CoverageSummary;
  
  // UI State
  loading: boolean;
  error: string | null;
  lastRefresh: Date | null;
}

/**
 * Hook actions
 */
export interface UseTemporaryAssignmentsActions {
  // Queries
  refresh: () => Promise<void>;
  getCoverage: (coverageId: string) => Promise<CoverageInfo | null>;
  getPumperCoverageStatus: (pumperId: string) => Promise<PumperCoverageInfo>;
  
  // Create operations
  createFixedCoverage: (input: FixedEndDateCoverageInput) => Promise<TemporaryCoverageResult>;
  createReminderCoverage: (input: ReminderBasedCoverageInput) => Promise<TemporaryCoverageResult>;
  
  // Modification operations
  extendCoverage: (
    coverageId: string,
    options: {
      newEndDate?: Date;
      newReminderDate?: Date;
      reason?: string;
    }
  ) => Promise<TemporaryCoverageResult>;
  
  endCoverage: (
    coverageId: string,
    endReason: CoverageEndReason,
    notes?: string
  ) => Promise<TemporaryCoverageResult>;
  
  makePermanent: (
    coverageId: string,
    reason?: string
  ) => Promise<TemporaryCoverageResult>;
  
  acknowledgeReminder: (coverageId: string) => Promise<void>;
  
  // Selectors
  getCoverageById: (coverageId: string) => CoverageInfo | undefined;
  getCoveragesByPumper: (pumperId: string, role: 'covering' | 'original') => CoverageInfo[];
  getActiveCoverageForWell: (wellId: string) => CoverageInfo | undefined;
  getCoveragesByUrgency: (urgency: CoverageUrgency) => CoverageInfo[];
  getCoveragesByReason: (reason: CoverageReason) => CoverageInfo[];
  
  // Utility
  canPumperProvideCoverage: (pumperId: string) => boolean;
  isPumperBeingCovered: (pumperId: string) => boolean;
  getWellsUnderCoverage: () => string[];
}

/**
 * Full hook return type
 */
export type UseTemporaryAssignmentsReturn = UseTemporaryAssignmentsState & UseTemporaryAssignmentsActions;

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Calculate days between two dates
 */
function daysBetween(start: Date, end: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((end.getTime() - start.getTime()) / msPerDay);
}

/**
 * Determine urgency level for a coverage
 */
function calculateUrgency(coverage: {
  endDate: Date | null;
  reminderDate: Date | null;
  reminderSent: boolean;
  status: TemporaryAssignmentStatus;
}): CoverageUrgency {
  const now = new Date();
  
  // Check reminder-based coverage first
  if (!coverage.endDate) {
    if (coverage.reminderDate && !coverage.reminderSent) {
      const reminderDays = daysBetween(now, coverage.reminderDate);
      if (reminderDays <= 0) {
        return 'reminder_due';
      }
    }
    return 'indefinite';
  }
  
  // Fixed end date coverage
  const daysRemaining = daysBetween(now, coverage.endDate);
  
  if (daysRemaining < 0) {
    return 'overdue';
  }
  if (daysRemaining === 0) {
    return 'ending_today';
  }
  if (daysRemaining <= 3) {
    return 'ending_soon';
  }
  
  return 'normal';
}

/**
 * Convert Firestore timestamp or Date to Date
 */
function toDate(value: Date | { seconds: number; nanoseconds: number } | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && 'seconds' in value) {
    return new Date(value.seconds * 1000);
  }
  return null;
}

/**
 * Transform raw coverage document to UI-friendly CoverageInfo
 */
function transformToCoverageInfo(doc: TemporaryAssignmentDocument): CoverageInfo {
  const now = new Date();
  const startDate = toDate(doc.startDate) ?? new Date();
  const endDate = toDate(doc.endDate);
  const reminderDate = toDate(doc.reminderDate);
  const endedAt = toDate(doc.endedAt) ?? undefined;
  const createdAt = toDate(doc.createdAt) ?? new Date();
  
  const daysElapsed = daysBetween(startDate, now);
  const totalDays = endDate ? daysBetween(startDate, endDate) : null;
  const daysRemaining = endDate ? daysBetween(now, endDate) : null;
  const daysUntilReminder = reminderDate ? daysBetween(now, reminderDate) : null;
  
  // Calculate progress percentage for fixed-end coverages
  let progressPercent: number | null = null;
  if (totalDays !== null && totalDays > 0) {
    progressPercent = Math.min(100, Math.max(0, (daysElapsed / totalDays) * 100));
  }
  
  return {
    id: doc.id,
    coveringPumperId: doc.coveringPumperId,
    coveringPumperName: doc.coveringPumperName,
    originalPumperId: doc.originalPumperId,
    originalPumperName: doc.originalPumperName,
    wellIds: doc.wellIds,
    wellNames: doc.wellNames ?? [],
    wellCount: doc.wellCount,
    reason: doc.reason,
    reasonNote: doc.reasonNote,
    startDate,
    endDate,
    reminderDate,
    endedAt,
    status: doc.status,
    urgency: calculateUrgency({
      endDate,
      reminderDate,
      reminderSent: doc.reminderSent ?? false,
      status: doc.status,
    }),
    isReminderBased: endDate === null,
    reminderSent: doc.reminderSent ?? false,
    endAction: (doc.endAction === 'reassign' ? 'reassign_original' : doc.endAction) as CoverageEndAction,
    endReason: doc.endReason,
    endedBy: doc.endedBy,
    endedByName: doc.endedByName,
    daysElapsed,
    daysRemaining,
    totalDays,
    progressPercent,
    daysUntilReminder,
    createdBy: doc.createdBy,
    createdByName: doc.createdByName,
    createdAt,
  };
}

/**
 * Calculate summary statistics from coverages
 */
function calculateSummary(coverages: CoverageInfo[]): CoverageSummary {
  const active = coverages.filter(c => c.status !== 'ended');
  
  const byReason: Record<CoverageReason, number> = {
    on_vacation: 0,
    sick: 0,
    injury: 0,
    training: 0,
    helping_crew: 0,
    other: 0,
  };
  
  for (const coverage of active) {
    byReason[coverage.reason] = (byReason[coverage.reason] || 0) + 1;
  }
  
  return {
    totalActive: active.length,
    endingToday: active.filter(c => c.urgency === 'ending_today').length,
    endingSoon: active.filter(c => c.urgency === 'ending_soon').length,
    remindersDue: active.filter(c => c.urgency === 'reminder_due').length,
    reminderBased: active.filter(c => c.isReminderBased).length,
    extended: active.filter(c => c.status === 'extended').length,
    byReason,
  };
}

/**
 * Cache key for localStorage
 */
const CACHE_KEY = 'temporaryAssignments';
const CACHE_TTL = 3 * 60 * 1000; // 3 minutes (shorter for time-sensitive data)

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HOOK
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * useTemporaryAssignments - Core hook for temporary coverage management
 * 
 * @param options - Hook configuration options
 * @returns State and actions for managing temporary coverages
 * 
 * @example
 * ```tsx
 * const {
 *   coverages,
 *   endingSoon,
 *   summary,
 *   createFixedCoverage,
 *   extendCoverage,
 * } = useTemporaryAssignments({ user, refreshInterval: 60000 });
 * ```
 */
export function useTemporaryAssignments(
  options: UseTemporaryAssignmentsOptions
): UseTemporaryAssignmentsReturn {
  const {
    user,
    refreshInterval = 0,
    coveringPumperId,
    originalPumperId,
    // These options reserved for future use (ended history, threshold customization)
    // includeEnded = false,
    // endingSoonThreshold = 3,
  } = options;
  
  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  
  const [coverages, setCoverages] = useState<CoverageInfo[]>([]);
  const [history] = useState<CoverageInfo[]>([]); // History loading deferred to dedicated action
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  // Refs for cleanup
  const mountedRef = useRef(true);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingRef = useRef(false);
  
  // Role checks
  const isManager = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'superadmin';
  const isPumper = user?.role === 'pumper';
  
  // ─────────────────────────────────────────────────────────────────────────────
  // DERIVED STATE (memoized)
  // ─────────────────────────────────────────────────────────────────────────────
  
  const activeCoverages = useMemo(() => 
    coverages.filter(c => c.status !== 'ended'),
    [coverages]
  );
  
  const endingSoon = useMemo(() => 
    activeCoverages.filter(c => 
      c.urgency === 'ending_soon' || 
      c.urgency === 'ending_today' ||
      c.urgency === 'overdue'
    ).sort((a, b) => {
      // Sort by days remaining (soonest first)
      const aRemaining = a.daysRemaining ?? Infinity;
      const bRemaining = b.daysRemaining ?? Infinity;
      return aRemaining - bRemaining;
    }),
    [activeCoverages]
  );
  
  const remindersDue = useMemo(() =>
    activeCoverages.filter(c => c.urgency === 'reminder_due'),
    [activeCoverages]
  );
  
  const summary = useMemo(() => 
    calculateSummary(coverages),
    [coverages]
  );
  
  // ─────────────────────────────────────────────────────────────────────────────
  // CACHING
  // ─────────────────────────────────────────────────────────────────────────────
  
  const getCachedData = useCallback((): CoverageInfo[] | null => {
    try {
      const key = getCompanyStorageKey(CACHE_KEY);
      const cached = localStorage.getItem(key);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      
      // Check TTL
      if (Date.now() - timestamp > CACHE_TTL) {
        localStorage.removeItem(key);
        return null;
      }
      
      // Restore dates
      return data.map((c: CoverageInfo) => ({
        ...c,
        startDate: new Date(c.startDate),
        endDate: c.endDate ? new Date(c.endDate) : null,
        reminderDate: c.reminderDate ? new Date(c.reminderDate) : null,
        endedAt: c.endedAt ? new Date(c.endedAt) : undefined,
        createdAt: new Date(c.createdAt),
      }));
    } catch {
      return null;
    }
  }, []);
  
  const setCachedData = useCallback((data: CoverageInfo[]) => {
    try {
      const key = getCompanyStorageKey(CACHE_KEY);
      localStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now(),
      }));
    } catch {
      // Ignore cache write failures
    }
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────────────────────────────────────
  
  const loadData = useCallback(async (showLoading = true) => {
    if (loadingRef.current) return;
    
    // Handle null user - set empty state and stop loading
    if (!user) {
      if (mountedRef.current) {
        setCoverages([]);
        setLoading(false);
      }
      return;
    }
    
    loadingRef.current = true;
    if (showLoading && mountedRef.current) {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Build query options
      const queryOptions: {
        coveringPumperId?: string;
        originalPumperId?: string;
        includeExtended?: boolean;
      } = {
        includeExtended: true,
      };
      
      // Apply filters
      if (coveringPumperId) {
        queryOptions.coveringPumperId = coveringPumperId;
      }
      if (originalPumperId) {
        queryOptions.originalPumperId = originalPumperId;
      }
      
      // For pumpers, filter to their own coverages
      if (isPumper && user.id && !isManager) {
        // Pumpers see coverages they're providing or receiving
        const [providing, receiving] = await Promise.all([
          getActiveCoverages({ coveringPumperId: user.id, includeExtended: true }),
          getActiveCoverages({ originalPumperId: user.id, includeExtended: true }),
        ]);
        
        // Merge and dedupe
        const allDocs = [...providing, ...receiving];
        const uniqueDocs = allDocs.filter((doc, index, self) => 
          index === self.findIndex(d => d.id === doc.id)
        );
        
        if (mountedRef.current) {
          const transformed = uniqueDocs.map(transformToCoverageInfo);
          setCoverages(transformed);
          setCachedData(transformed);
          setLastRefresh(new Date());
        }
      } else {
        // Managers see all coverages
        const docs = await getActiveCoverages(queryOptions);
        
        if (mountedRef.current) {
          const transformed = docs.map(transformToCoverageInfo);
          setCoverages(transformed);
          setCachedData(transformed);
          setLastRefresh(new Date());
        }
      }
      
    } catch (err: unknown) {
      console.error('❌ Failed to load temporary assignments:', err);
      
      // Try cache fallback
      const cached = getCachedData();
      if (cached && mountedRef.current) {
        setCoverages(cached);
        setError('Using cached data - network unavailable');
      } else if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load coverages');
      }
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, isManager, isPumper, coveringPumperId, originalPumperId, getCachedData, setCachedData]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  
  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    loadData();
    
    return () => {
      mountedRef.current = false;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [loadData]);
  
  // Auto-refresh
  useEffect(() => {
    if (refreshInterval > 0 && refreshTimerRef.current === null) {
      refreshTimerRef.current = setInterval(() => {
        loadData(false);
      }, refreshInterval);
    }
    
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [refreshInterval, loadData]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const refresh = useCallback(async () => {
    await loadData(true);
  }, [loadData]);
  
  const getCoverage = useCallback(async (coverageId: string): Promise<CoverageInfo | null> => {
    try {
      const doc = await getCoverageById(coverageId);
      if (!doc) return null;
      return transformToCoverageInfo(doc);
    } catch {
      return null;
    }
  }, []);
  
  const getPumperCoverageStatus = useCallback(async (pumperId: string): Promise<PumperCoverageInfo> => {
    const [providing, receiving] = await Promise.all([
      getActiveCoverageByPumper(pumperId),
      getActiveCoverageForPumper(pumperId),
    ]);
    
    return {
      id: pumperId,
      name: providing?.coveringPumperName || receiving?.originalPumperName || 'Unknown',
      providingCoverage: providing ? transformToCoverageInfo(providing) : null,
      receivingCoverage: receiving ? transformToCoverageInfo(receiving) : null,
      canProvideCoverage: !providing,
      isBeingCovered: !!receiving,
    };
  }, []);
  
  const createFixedCoverage = useCallback(async (
    input: FixedEndDateCoverageInput
  ): Promise<TemporaryCoverageResult> => {
    try {
      const result = await createTemporaryAssignment(input);
      
      if (result.success) {
        // Refresh to get updated state
        await loadData(false);
      }
      
      return result;
    } catch (err: unknown) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to create coverage',
      };
    }
  }, [loadData]);
  
  const createReminderCoverage = useCallback(async (
    input: ReminderBasedCoverageInput
  ): Promise<TemporaryCoverageResult> => {
    try {
      const result = await createReminderBasedCoverage(input);
      
      if (result.success) {
        await loadData(false);
      }
      
      return result;
    } catch (err: unknown) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to create coverage',
      };
    }
  }, [loadData]);
  
  const extendCoverageAction = useCallback(async (
    coverageId: string,
    options: {
      newEndDate?: Date;
      newReminderDate?: Date;
      reason?: string;
    }
  ): Promise<TemporaryCoverageResult> => {
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }
    
    try {
      const result = await extendTemporaryAssignment(coverageId, {
        ...options,
        performedBy: user.id,
        performedByName: user.role || 'User',
      });
      
      if (result.success) {
        // Optimistic update
        setCoverages(prev => prev.map(c => {
          if (c.id !== coverageId) return c;
          
          const now = new Date();
          const newEndDate = options.newEndDate || c.endDate;
          const newReminderDate = options.newReminderDate || c.reminderDate;
          
          return {
            ...c,
            status: 'extended' as TemporaryAssignmentStatus,
            endDate: newEndDate,
            reminderDate: newReminderDate,
            daysRemaining: newEndDate ? daysBetween(now, newEndDate) : null,
            totalDays: newEndDate ? daysBetween(c.startDate, newEndDate) : null,
            daysUntilReminder: newReminderDate ? daysBetween(now, newReminderDate) : null,
            urgency: calculateUrgency({
              endDate: newEndDate,
              reminderDate: newReminderDate,
              reminderSent: false,
              status: 'extended',
            }),
          };
        }));
        
        // Background refresh for accurate state
        setTimeout(() => loadData(false), 1000);
      }
      
      return result;
    } catch (err: unknown) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to extend coverage',
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, loadData]);
  
  const endCoverageAction = useCallback(async (
    coverageId: string,
    endReason: CoverageEndReason,
    notes?: string
  ): Promise<TemporaryCoverageResult> => {
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }
    
    try {
      const result = await endTemporaryAssignment(coverageId, {
        endReason,
        performedBy: user.id,
        performedByName: user.role || 'User',
        notes,
      });
      
      if (result.success) {
        // Optimistic update - move to ended
        setCoverages(prev => prev.map(c => {
          if (c.id !== coverageId) return c;
          return {
            ...c,
            status: 'ended' as TemporaryAssignmentStatus,
            endedAt: new Date(),
            endReason,
            endedBy: user.id,
            endedByName: user.role,
          };
        }));
        
        // Background refresh
        setTimeout(() => loadData(false), 1000);
      }
      
      return result;
    } catch (err: unknown) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to end coverage',
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, loadData]);
  
  const makePermanentAction = useCallback(async (
    coverageId: string,
    reason?: string
  ): Promise<TemporaryCoverageResult> => {
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }
    
    try {
      const result = await makeCoveragePermanent(coverageId, {
        reason,
        performedBy: user.id,
        performedByName: user.role || 'User',
      });
      
      if (result.success) {
        // Optimistic update
        setCoverages(prev => prev.map(c => {
          if (c.id !== coverageId) return c;
          return {
            ...c,
            status: 'ended' as TemporaryAssignmentStatus,
            endedAt: new Date(),
            endReason: 'made_permanent' as CoverageEndReason,
            endedBy: user.id,
            endedByName: user.role,
          };
        }));
        
        // Background refresh
        setTimeout(() => loadData(false), 1000);
      }
      
      return result;
    } catch (err: unknown) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to make permanent',
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, loadData]);
  
  const acknowledgeReminder = useCallback(async (coverageId: string): Promise<void> => {
    if (!user) return;
    
    try {
      await markReminderSent(coverageId, user.id);
      
      // Optimistic update
      setCoverages(prev => prev.map(c => {
        if (c.id !== coverageId) return c;
        return {
          ...c,
          reminderSent: true,
          urgency: c.isReminderBased ? 'indefinite' : c.urgency,
        };
      }));
    } catch (err: unknown) {
      console.error('Failed to acknowledge reminder:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // SELECTORS (memoized)
  // ─────────────────────────────────────────────────────────────────────────────
  
  const getCoverageByIdSelector = useCallback((coverageId: string): CoverageInfo | undefined => {
    return coverages.find(c => c.id === coverageId);
  }, [coverages]);
  
  const getCoveragesByPumper = useCallback((
    pumperId: string, 
    role: 'covering' | 'original'
  ): CoverageInfo[] => {
    if (role === 'covering') {
      return coverages.filter(c => c.coveringPumperId === pumperId);
    }
    return coverages.filter(c => c.originalPumperId === pumperId);
  }, [coverages]);
  
  const getActiveCoverageForWell = useCallback((wellId: string): CoverageInfo | undefined => {
    return activeCoverages.find(c => c.wellIds.includes(wellId));
  }, [activeCoverages]);
  
  const getCoveragesByUrgency = useCallback((urgency: CoverageUrgency): CoverageInfo[] => {
    return coverages.filter(c => c.urgency === urgency);
  }, [coverages]);
  
  const getCoveragesByReason = useCallback((reason: CoverageReason): CoverageInfo[] => {
    return coverages.filter(c => c.reason === reason);
  }, [coverages]);
  
  const canPumperProvideCoverage = useCallback((pumperId: string): boolean => {
    // A pumper can provide coverage if they're not already providing coverage
    return !activeCoverages.some(c => c.coveringPumperId === pumperId);
  }, [activeCoverages]);
  
  const isPumperBeingCovered = useCallback((pumperId: string): boolean => {
    return activeCoverages.some(c => c.originalPumperId === pumperId);
  }, [activeCoverages]);
  
  const getWellsUnderCoverage = useCallback((): string[] => {
    const wellIds = new Set<string>();
    for (const coverage of activeCoverages) {
      for (const wellId of coverage.wellIds) {
        wellIds.add(wellId);
      }
    }
    return Array.from(wellIds);
  }, [activeCoverages]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // RETURN
  // ─────────────────────────────────────────────────────────────────────────────
  
  return {
    // State
    coverages: activeCoverages,
    endingSoon,
    remindersDue,
    history,
    summary,
    loading,
    error,
    lastRefresh,
    
    // Actions
    refresh,
    getCoverage,
    getPumperCoverageStatus,
    createFixedCoverage,
    createReminderCoverage,
    extendCoverage: extendCoverageAction,
    endCoverage: endCoverageAction,
    makePermanent: makePermanentAction,
    acknowledgeReminder,
    
    // Selectors
    getCoverageById: getCoverageByIdSelector,
    getCoveragesByPumper,
    getActiveCoverageForWell,
    getCoveragesByUrgency,
    getCoveragesByReason,
    
    // Utility
    canPumperProvideCoverage,
    isPumperBeingCovered,
    getWellsUnderCoverage,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONVENIENCE HOOKS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Hook for getting coverage status badges/info
 * Useful for displaying coverage indicators on wells or pumpers
 */
export function useCoverageIndicator(wellId: string, user: { id: string; role: string } | null) {
  const { getActiveCoverageForWell, loading } = useTemporaryAssignments({ user });
  
  const coverage = useMemo(() => 
    getActiveCoverageForWell(wellId),
    [getActiveCoverageForWell, wellId]
  );
  
  return {
    isUnderCoverage: !!coverage,
    coverage,
    loading,
    badgeText: coverage 
      ? `Covered by ${coverage.coveringPumperName}` 
      : null,
    urgencyColor: coverage
      ? coverage.urgency === 'ending_today' || coverage.urgency === 'overdue'
        ? 'red'
        : coverage.urgency === 'ending_soon'
          ? 'amber'
          : 'blue'
      : null,
  };
}

/**
 * Hook for pumper-specific coverage info
 * Shows if pumper is providing or receiving coverage
 */
export function usePumperCoverage(pumperId: string, user: { id: string; role: string } | null) {
  const { getCoveragesByPumper, canPumperProvideCoverage, isPumperBeingCovered, loading } = 
    useTemporaryAssignments({ user });
  
  const providingCoverages = useMemo(() => 
    getCoveragesByPumper(pumperId, 'covering'),
    [getCoveragesByPumper, pumperId]
  );
  
  const receivingCoverages = useMemo(() => 
    getCoveragesByPumper(pumperId, 'original'),
    [getCoveragesByPumper, pumperId]
  );
  
  return {
    providingCoverages,
    receivingCoverages,
    isProvidingCoverage: providingCoverages.length > 0,
    isReceivingCoverage: receivingCoverages.length > 0,
    canProvideCoverage: canPumperProvideCoverage(pumperId),
    isBeingCovered: isPumperBeingCovered(pumperId),
    loading,
    // Quick status for badges
    statusBadge: providingCoverages.length > 0
      ? `Covering ${providingCoverages[0].originalPumperName}`
      : receivingCoverages.length > 0
        ? `Being covered by ${receivingCoverages[0].coveringPumperName}`
        : null,
  };
}

export default useTemporaryAssignments;
